import { createFileRoute, Link } from '@tanstack/react-router'
import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { usePermission } from '@/lib/permissions'
import { useIsDarkMode } from '@/lib/hooks/use-dark-mode'
import { AccessDenied } from '@/components/permission-guard'
import {
  useMapConfig,
  useEvent,
  useLiveStats,
  useLiveTransactions,
  useLiveHeatmap,
  useLiveLeaderboard,
} from '@/lib/api/queries'
import type { MapConfig, LiveStatsResponse } from '@/lib/api/types'
import mapboxgl from 'mapbox-gl'
import { FALLBACK_MAP_CENTER, cachedVenueCenter, geocodeVenueCenter, type LngLat } from '@/lib/venue-center'
import 'mapbox-gl/dist/mapbox-gl.css'
import {
  Users,
  TrendingUp,
  TrendingDown,
  Minus,
  Zap,
  ArrowUpRight,
  ArrowDownRight,
  Flame,
  ExternalLink,
  MapPin,
  ShoppingBag,
  Loader2,
  Settings,
  X,
} from 'lucide-react'

export const Route = createFileRoute('/_authenticated/events/$eventId/live-stats')({
  component: LiveStatsPage,
})

function LiveStatsPage() {
  const { eventId } = Route.useParams()
  const eventIdNum = Number(eventId)
  const isDark = useIsDarkMode()

  const canViewLiveStats = usePermission('can_view_live_stats')
  const { data: event } = useEvent(eventIdNum)
  const { data: mapConfig, isLoading: configLoading } = useMapConfig(eventIdNum)
  const [activeWindow, setActiveWindow] = useState(45)
  const [showSettings, setShowSettings] = useState(false)
  const [showHeatmap, setShowHeatmap] = useState(false)

  const { data: liveData, isLoading: liveLoading } = useLiveStats(eventIdNum, activeWindow)
  const allTransactions = useLiveTransactions(eventIdNum, activeWindow)
  const heatmapVendors = useLiveHeatmap(eventIdNum, activeWindow)
  const productLeaderboard = useLiveLeaderboard(eventIdNum, activeWindow)
  const leaderboardLoading = liveLoading && productLeaderboard.length === 0

  const hasMap = !!mapConfig && (
    !!mapConfig.map_boundary?.coordinates?.length ||
    !!mapConfig.map_overlay_image ||
    (mapConfig.vendor_locations?.length ?? 0) > 0
  )

  if (!canViewLiveStats) {
    return <AccessDenied message="You don't have permission to view live stats." />
  }

  return (
    <div className="flex flex-col lg:relative h-[calc(100vh-4rem)] overflow-hidden bg-neutral-100 dark:bg-neutral-950">
      {/* Map layer — stacked on mobile, full-bleed on desktop */}
      <div className="relative h-[40vh] shrink-0 lg:absolute lg:inset-0 lg:h-auto">
        {configLoading ? (
          <div className="flex items-center justify-center h-full bg-neutral-100 dark:bg-neutral-950">
            <Loader2 className="w-8 h-8 animate-spin text-neutral-300 dark:text-white/30" />
          </div>
        ) : hasMap ? (
          <LiveMapView
            eventId={eventIdNum}
            mapConfig={mapConfig}
            liveData={liveData ?? null}
            heatmapVendors={heatmapVendors}
            showHeatmap={showHeatmap}
            onToggleHeatmap={() => setShowHeatmap((v) => !v)}
            isDark={isDark}
            venueAddress={[event?.address, event?.city, event?.zip_code, event?.country].filter(Boolean).join(', ') || undefined}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full bg-neutral-100 dark:bg-neutral-950 gap-3">
            <MapPin className="w-12 h-12 text-neutral-300 dark:text-white/20" />
            <p className="text-neutral-400 dark:text-white/40 text-sm">No site map configured for this event.</p>
            <Link
              to="/events/$eventId/site-map"
              params={{ eventId: String(eventIdNum) }}
              className="text-blue-600 dark:text-blue-400 text-sm font-medium hover:underline"
            >
              Set up Site Map
            </Link>
          </div>
        )}

        {/* Map control buttons — top-left */}
        {hasMap && !configLoading && (
          <div className="absolute top-3 left-3 z-20 flex gap-2">
            <button
              onClick={() => setShowHeatmap((v) => !v)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium shadow-lg transition-colors ${
                showHeatmap
                  ? 'bg-orange-500/80 text-white border border-orange-400/30 hover:bg-orange-500/90'
                  : 'bg-white/80 dark:bg-black/40 backdrop-blur-xl text-neutral-700 dark:text-white border border-neutral-200/60 dark:border-white/10 hover:bg-neutral-100 dark:hover:bg-white/10'
              }`}
            >
              <Flame className="w-3.5 h-3.5" />
              Heatmap
            </button>
            <Link
              to="/events/$eventId/site-map"
              params={{ eventId: String(eventIdNum) }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/80 dark:bg-black/40 backdrop-blur-xl text-neutral-700 dark:text-white border border-neutral-200/60 dark:border-white/10 hover:bg-neutral-100 dark:hover:bg-white/10 shadow-lg"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Edit Map
            </Link>
            <button
              onClick={() => setShowSettings(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/80 dark:bg-black/40 backdrop-blur-xl text-neutral-700 dark:text-white border border-neutral-200/60 dark:border-white/10 hover:bg-neutral-100 dark:hover:bg-white/10 shadow-lg"
            >
              <Settings className="w-3.5 h-3.5" />
              Settings
            </button>
          </div>
        )}
      </div>

      {/* Data panel — scrollable below map on mobile, glass overlay on desktop */}
      <div className="flex-1 overflow-y-auto lg:absolute lg:inset-0 lg:pointer-events-none lg:z-10">
        <div className="p-3 flex flex-col gap-3 lg:ml-auto lg:w-[45%] lg:h-full lg:overflow-y-auto lg:pointer-events-auto">
          {/* Stats Row */}
          <StatsBar liveData={liveData ?? null} currency={event?.currency || 'USD'} />

          {/* Transaction Feed */}
          <TransactionFeed
            transactions={allTransactions}
            currency={event?.currency || 'USD'}
            activeWindow={activeWindow}
          />

          {/* Product Leaderboard */}
          <ProductLeaderboard
            products={productLeaderboard}
            currency={event?.currency || 'USD'}
            isLoading={leaderboardLoading}
          />

          {/* Top Movers */}
          <TopMovers
            movers={liveData?.trending?.top_movers || []}
          />
        </div>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <LiveSettingsModal
          activeWindow={activeWindow}
          onChangeWindow={setActiveWindow}
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  )
}

// ────────────────────── Settings Modal ──────────────────────

const ACTIVE_WINDOW_OPTIONS = [
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 45, label: '45 min' },
  { value: 60, label: '60 min' },
] as const

function LiveSettingsModal({
  activeWindow,
  onChangeWindow,
  onClose,
}: {
  activeWindow: number
  onChangeWindow: (value: number) => void
  onClose: () => void
}) {
  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative w-full max-w-sm bg-white/90 dark:bg-black/60 backdrop-blur-2xl border border-neutral-200/60 dark:border-white/15 rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200/60 dark:border-white/10">
          <h2 className="text-sm font-semibold text-neutral-900 dark:text-white">Live Stats Settings</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg text-neutral-400 dark:text-white/40 hover:text-neutral-700 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-white/10 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs text-neutral-500 dark:text-white/50 uppercase tracking-wider mb-2">
              Active Customers Window
            </label>
            <div className="grid grid-cols-4 gap-2">
              {ACTIVE_WINDOW_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => onChangeWindow(opt.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    activeWindow === opt.value
                      ? 'bg-blue-50 dark:bg-white/20 border-blue-300 dark:border-white/30 text-blue-700 dark:text-white'
                      : 'bg-neutral-50 dark:bg-white/5 border-neutral-200 dark:border-white/10 text-neutral-500 dark:text-white/50 hover:bg-neutral-100 dark:hover:bg-white/10 hover:text-neutral-700 dark:hover:text-white/70'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-neutral-400 dark:text-white/30 mt-2">
              How far back to count customers as &quot;active&quot;
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-neutral-200/60 dark:border-white/10 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-1.5 rounded-lg text-sm font-medium bg-neutral-100 dark:bg-white/10 text-neutral-700 dark:text-white border border-neutral-200 dark:border-white/10 hover:bg-neutral-200 dark:hover:bg-white/15 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )
}

// ────────────────────── Live Map View ──────────────────────

function LiveMapView({
  eventId: _eventId,
  mapConfig,
  liveData,
  heatmapVendors,
  showHeatmap,
  onToggleHeatmap: _onToggleHeatmap,
  isDark,
  venueAddress,
}: {
  eventId: number
  mapConfig: MapConfig
  liveData: LiveStatsResponse | null
  heatmapVendors: LiveStatsResponse['vendors']
  showHeatmap: boolean
  onToggleHeatmap: () => void
  isDark: boolean
  venueAddress?: string
}) {
  const mapContainerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const markersRef = useRef<mapboxgl.Marker[]>([])
  const overlayImgRef = useRef<HTMLImageElement | null>(null)
  const prevTransactionIdsRef = useRef<Set<number>>(new Set())
  const isInitialLoadRef = useRef(true)

  // Native Mapbox blip animation refs
  const blipSourceRef = useRef<mapboxgl.GeoJSONSource | null>(null)
  const blipAnimFrameRef = useRef<number | null>(null)
  const activeBlipsRef = useRef<Array<{
    id: string
    lng: number
    lat: number
    startTime: number
    dollars: number
  }>>([])

  const mapboxToken = import.meta.env.VITE_MAPBOX_TOKEN

  const safeRemoveLayer = (map: mapboxgl.Map, id: string) => {
    try { if (map.getStyle() && map.getLayer(id)) map.removeLayer(id) } catch (_) {}
  }
  const safeRemoveSource = (map: mapboxgl.Map, id: string) => {
    try { if (map.getStyle() && map.getSource(id)) map.removeSource(id) } catch (_) {}
  }
  // @ts-expect-error reserved for future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const safeHasSource = (map: mapboxgl.Map, id: string): boolean => {
    try { return !!(map.getStyle() && map.getSource(id)) } catch (_) { return false }
  }

  // Derive bounding box from site boundary polygon
  const boundaryBounds = useMemo((): [[number, number], [number, number]] | null => {
    const coords = mapConfig?.map_boundary?.coordinates?.[0]
    if (!coords || coords.length < 3) return null
    let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity
    coords.forEach(([lng, lat]: number[]) => {
      if (lng < minLng) minLng = lng
      if (lng > maxLng) maxLng = lng
      if (lat < minLat) minLat = lat
      if (lat > maxLat) maxLat = lat
    })
    return [[minLat, minLng], [maxLat, maxLng]]
  }, [mapConfig?.map_boundary])

  // Synchronously-known center: boundary midpoint or the saved map center.
  const knownCenter = useMemo<LngLat | null>(() => {
    if (boundaryBounds) {
      const cLng = (boundaryBounds[0][1] + boundaryBounds[1][1]) / 2
      const cLat = (boundaryBounds[0][0] + boundaryBounds[1][0]) / 2
      return [cLng, cLat]
    }
    if (mapConfig?.map_center_longitude && mapConfig?.map_center_latitude) {
      return [mapConfig.map_center_longitude, mapConfig.map_center_latitude]
    }
    return null
  }, [boundaryBounds, mapConfig?.map_center_longitude, mapConfig?.map_center_latitude])

  // Resolve BEFORE constructing the map — no fallback-city flash, no
  // cross-continent flyTo. Geocode result is cached per address.
  const [resolvedCenter, setResolvedCenter] = useState<LngLat | null>(
    () => knownCenter ?? cachedVenueCenter(venueAddress),
  )
  useEffect(() => {
    if (knownCenter && !resolvedCenter) setResolvedCenter(knownCenter)
  }, [knownCenter, resolvedCenter])
  useEffect(() => {
    if (resolvedCenter || !mapboxToken) return
    if (!venueAddress) {
      setResolvedCenter(FALLBACK_MAP_CENTER)
      return
    }
    let cancelled = false
    geocodeVenueCenter(venueAddress, mapboxToken).then((center) => {
      if (!cancelled) setResolvedCenter(center ?? FALLBACK_MAP_CENTER)
    })
    return () => { cancelled = true }
  }, [resolvedCenter, venueAddress, mapboxToken])

  const defaultZoom = mapConfig?.map_zoom_level ?? (boundaryBounds ? 16 : 15)


  // Helper: convert percentage coords to [lng, lat]
  const toMapCoords = useCallback((x: number, y: number): [number, number] | null => {
    if (!boundaryBounds || x == null || y == null) return null
    const lng = boundaryBounds[0][1] + (x / 100) * (boundaryBounds[1][1] - boundaryBounds[0][1])
    const lat = boundaryBounds[1][0] - (y / 100) * (boundaryBounds[1][0] - boundaryBounds[0][0])
    return [lng, lat]
  }, [boundaryBounds])

  const mapStyleUrl = isDark
    ? 'mapbox://styles/mapbox/dark-v11'
    : 'mapbox://styles/mapbox/light-v11'

  // Refs that hold the latest "add layers" functions so the style.load listener can call them
  const addBoundaryRef = useRef<(() => void) | null>(null)
  const addBlipLayersRef = useRef<(() => void) | null>(null)
  const addHeatmapRef = useRef<(() => void) | null>(null)
  const addVendorMarkersRef = useRef<(() => void) | null>(null)

  // Initialize map (only once — style swaps happen in a separate effect)
  useEffect(() => {
    if (!mapContainerRef.current || !mapboxToken || !resolvedCenter) return

    mapboxgl.accessToken = mapboxToken

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: mapStyleUrl,
      center: resolvedCenter,
      zoom: defaultZoom,
    })

    map.addControl(new mapboxgl.NavigationControl(), 'bottom-right')
    mapRef.current = map

    map.on('load', () => {
      if (boundaryBounds) {
        const fitBounds = new mapboxgl.LngLatBounds()
        fitBounds.extend([boundaryBounds[0][1], boundaryBounds[0][0]])
        fitBounds.extend([boundaryBounds[1][1], boundaryBounds[1][0]])
        // On desktop (lg+), offset right padding so boundary centers in the left ~55% (glass panel covers right 45%)
        // On mobile, the panel is below the map so center normally
        const isDesktop = window.matchMedia('(min-width: 1024px)').matches
        const containerWidth = mapContainerRef.current?.clientWidth ?? 1000
        const panelWidth = isDesktop ? containerWidth * 0.45 : 0
        map.fitBounds(fitBounds, {
          padding: { top: 20, bottom: 20, left: 20, right: panelWidth + 20 },
          maxZoom: 19,
        })
      }
    })

    // Persistent style.load listener — re-adds all custom layers after any setStyle() call
    map.on('style.load', () => {
      addBoundaryRef.current?.()
      addBlipLayersRef.current?.()
      addHeatmapRef.current?.()
      addVendorMarkersRef.current?.()
    })

    return () => {
      map.remove()
      mapRef.current = null
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapboxToken, boundaryBounds, resolvedCenter])


  // Theme swap — only calls setStyle when the style URL actually changes
  const prevStyleRef = useRef(mapStyleUrl)
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (prevStyleRef.current === mapStyleUrl) return
    prevStyleRef.current = mapStyleUrl
    map.setStyle(mapStyleUrl)
  }, [mapStyleUrl])

  // Resize on container changes
  useEffect(() => {
    const container = mapContainerRef.current
    const map = mapRef.current
    if (!container || !map) return

    const observer = new ResizeObserver(() => map.resize())
    observer.observe(container)
    return () => observer.disconnect()
  })

  // Image overlay
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    if (overlayImgRef.current) {
      overlayImgRef.current.remove()
      overlayImgRef.current = null
    }

    if (!mapConfig?.map_overlay_image || !boundaryBounds) return

    const img = document.createElement('img')
    img.src = mapConfig.map_overlay_image
    img.style.position = 'absolute'
    img.style.pointerEvents = 'none'
    img.style.opacity = String(mapConfig.map_overlay_opacity ?? 0.9)
    img.style.zIndex = '1'
    img.style.transformOrigin = 'center center'
    overlayImgRef.current = img
    map.getCanvasContainer().appendChild(img)

    const swLng = boundaryBounds[0][1]
    const swLat = boundaryBounds[0][0]
    const neLng = boundaryBounds[1][1]
    const neLat = boundaryBounds[1][0]

    const rot = mapConfig.map_overlay_rotation ?? 0
    const sc = mapConfig.map_overlay_scale ?? 1.0

    const updatePosition = () => {
      if (!overlayImgRef.current) return
      const topLeft = map.project([swLng, neLat])
      const bottomRight = map.project([neLng, swLat])
      overlayImgRef.current.style.left = topLeft.x + 'px'
      overlayImgRef.current.style.top = topLeft.y + 'px'
      overlayImgRef.current.style.width = (bottomRight.x - topLeft.x) + 'px'
      overlayImgRef.current.style.height = (bottomRight.y - topLeft.y) + 'px'
      overlayImgRef.current.style.transform = `rotate(${rot}deg) scale(${sc})`
    }

    updatePosition()
    map.on('move', updatePosition)
    map.on('resize', updatePosition)

    return () => {
      map.off('move', updatePosition)
      map.off('resize', updatePosition)
      if (overlayImgRef.current) {
        overlayImgRef.current.remove()
        overlayImgRef.current = null
      }
    }
  }, [mapConfig?.map_overlay_image, mapConfig?.map_overlay_opacity, mapConfig?.map_overlay_rotation, mapConfig?.map_overlay_scale, boundaryBounds])

  // Boundary polygon — store the add function in a ref so style.load can re-invoke it
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    const addBoundaryLayer = () => {
      if (!mapRef.current) return
      const m = mapRef.current
      safeRemoveLayer(m, 'live-boundary-line')
      safeRemoveLayer(m, 'live-boundary-fill')
      safeRemoveSource(m, 'live-boundary')

      if (!mapConfig?.map_boundary) return

      const coords = (mapConfig.map_boundary as any).coordinates
      if (!coords || !coords[0] || coords[0].length < 3) return

      m.addSource('live-boundary', {
        type: 'geojson',
        data: {
          type: 'Feature',
          properties: {},
          geometry: mapConfig.map_boundary as any,
        },
      })

      m.addLayer({
        id: 'live-boundary-fill',
        type: 'fill',
        source: 'live-boundary',
        paint: {
          'fill-color': '#8b5cf6',
          'fill-opacity': 0.12,
        },
      })

      m.addLayer({
        id: 'live-boundary-line',
        type: 'line',
        source: 'live-boundary',
        paint: {
          'line-color': '#8b5cf6',
          'line-width': 2,
          'line-dasharray': [2, 2],
        },
      })
    }

    addBoundaryRef.current = addBoundaryLayer

    if (map.isStyleLoaded()) {
      addBoundaryLayer()
    } else {
      map.once('load', addBoundaryLayer)
    }

    return () => {
      addBoundaryRef.current = null
      safeRemoveLayer(map, 'live-boundary-line')
      safeRemoveLayer(map, 'live-boundary-fill')
      safeRemoveSource(map, 'live-boundary')
    }
  }, [mapConfig?.map_boundary])

  // Render vendor markers — DOM markers survive setStyle() but store re-add in ref for safety
  useEffect(() => {
    const map = mapRef.current
    if (!map || !boundaryBounds) return

    const addMarkers = () => {
      if (!mapRef.current) return
      const m = mapRef.current

      markersRef.current.forEach((mk) => mk.remove())
      markersRef.current = []

      const placedVendors = (mapConfig?.vendor_locations || []).filter((v) => v.x != null && v.y != null)
      const sorted = [...placedVendors].sort((a, b) => (a.vendor_name || '').localeCompare(b.vendor_name || ''))
      const numberMap = new Map<number, number>()
      sorted.forEach((v, i) => numberMap.set(v.vendor_event_id, i + 1))

      placedVendors.forEach((vendor) => {
        const coords = toMapCoords(vendor.x, vendor.y)
        if (!coords) return

        const num = numberMap.get(vendor.vendor_event_id) ?? 0

        const el = document.createElement('div')
        el.style.cssText = `
          width: 24px; height: 24px; border-radius: 50%;
          background: #3b82f6; border: 2px solid white;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          box-sizing: border-box;
          cursor: pointer; color: white; font-size: 9px;
          font-weight: 700; line-height: 20px;
          text-align: center; user-select: none;
          z-index: 2;
        `
        el.textContent = String(num)
        el.title = `#${num} ${vendor.vendor_name} (${vendor.booth})`

        const popup = new mapboxgl.Popup({ offset: 15, closeButton: false, closeOnClick: false })
          .setHTML(
            `<div style="padding:4px"><strong>#${num} ${vendor.vendor_name}</strong><br/><small>${vendor.booth}</small></div>`
          )

        const marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
          .setLngLat(coords)
          .setPopup(popup)
          .addTo(m)

        el.addEventListener('mouseenter', () => marker.togglePopup())
        el.addEventListener('mouseleave', () => marker.togglePopup())

        markersRef.current.push(marker)
      })
    }

    addVendorMarkersRef.current = addMarkers
    addMarkers()

    return () => {
      addVendorMarkersRef.current = null
      markersRef.current.forEach((mk) => mk.remove())
      markersRef.current = []
    }
  }, [mapConfig?.vendor_locations, boundaryBounds, toMapCoords])

  // Blip animation loop — drives the native Mapbox circle layers
  const BLIP_DURATION = 2000
  const animateBlips = useCallback(() => {
    const source = blipSourceRef.current
    if (!source) return

    const now = performance.now()

    // Remove expired blips
    activeBlipsRef.current = activeBlipsRef.current.filter(
      (b) => (now - b.startTime) / BLIP_DURATION < 1
    )

    const features = activeBlipsRef.current.map((b) => ({
      type: 'Feature' as const,
      properties: {
        progress: Math.max(0, Math.min((now - b.startTime) / BLIP_DURATION, 1)),
        dollars: b.dollars,
      },
      geometry: {
        type: 'Point' as const,
        coordinates: [b.lng, b.lat],
      },
    }))

    source.setData({ type: 'FeatureCollection', features } as any)

    if (activeBlipsRef.current.length > 0) {
      blipAnimFrameRef.current = requestAnimationFrame(animateBlips)
    } else {
      blipAnimFrameRef.current = null
    }
  }, [])

  // Set up blip GeoJSON source + circle layers — store in ref for style.load re-add
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    const setup = () => {
      if (!mapRef.current) return
      const m = mapRef.current

      // Clean up any existing blip layers first
      safeRemoveLayer(m, 'blip-dot')
      safeRemoveLayer(m, 'blip-ring')
      safeRemoveSource(m, 'blip-source')

      m.addSource('blip-source', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      })

      // Outer expanding ring
      m.addLayer({
        id: 'blip-ring',
        type: 'circle',
        source: 'blip-source',
        paint: {
          'circle-radius': [
            'interpolate', ['linear'], ['get', 'progress'],
            0, 6,
            1, 35,
          ],
          'circle-color': '#22c55e',
          'circle-opacity': [
            'interpolate', ['linear'], ['get', 'progress'],
            0, 0.6,
            1, 0,
          ],
          'circle-stroke-width': [
            'interpolate', ['linear'], ['get', 'progress'],
            0, 2,
            1, 0.5,
          ],
          'circle-stroke-color': 'rgba(255, 255, 255, 0.7)',
          'circle-stroke-opacity': [
            'interpolate', ['linear'], ['get', 'progress'],
            0, 0.8,
            1, 0,
          ],
        },
      })

      // Inner solid dot
      m.addLayer({
        id: 'blip-dot',
        type: 'circle',
        source: 'blip-source',
        paint: {
          'circle-radius': [
            'interpolate', ['linear'], ['get', 'progress'],
            0, 5,
            0.15, 8,
            1, 3,
          ],
          'circle-color': '#22c55e',
          'circle-opacity': [
            'interpolate', ['linear'], ['get', 'progress'],
            0, 0.95,
            0.7, 0.6,
            1, 0,
          ],
          'circle-stroke-width': 1.5,
          'circle-stroke-color': 'white',
          'circle-stroke-opacity': [
            'interpolate', ['linear'], ['get', 'progress'],
            0, 1,
            0.5, 0.5,
            1, 0,
          ],
        },
      })

      blipSourceRef.current = m.getSource('blip-source') as mapboxgl.GeoJSONSource
    }

    addBlipLayersRef.current = setup

    if (map.isStyleLoaded()) {
      setup()
    } else {
      map.once('load', setup)
    }

    return () => {
      addBlipLayersRef.current = null
      if (blipAnimFrameRef.current) {
        cancelAnimationFrame(blipAnimFrameRef.current)
        blipAnimFrameRef.current = null
      }
      safeRemoveLayer(map, 'blip-dot')
      safeRemoveLayer(map, 'blip-ring')
      safeRemoveSource(map, 'blip-source')
      blipSourceRef.current = null
    }
  }, [mapboxToken, boundaryBounds])

  // Transaction blips — push new blips into the animation system
  useEffect(() => {
    if (!boundaryBounds || !liveData?.transactions?.length) return

    // Build a lookup from vendor_event_id → coordinates
    const vendorCoords = new Map<number, [number, number]>()
    for (const v of (liveData.vendors || [])) {
      if (v.x != null && v.y != null) {
        const coords = toMapCoords(v.x, v.y)
        if (coords) vendorCoords.set(v.vendor_event_id, coords)
      }
    }
    for (const v of (mapConfig?.vendor_locations || [])) {
      if (!vendorCoords.has(v.vendor_event_id) && v.x != null && v.y != null) {
        const coords = toMapCoords(v.x, v.y)
        if (coords) vendorCoords.set(v.vendor_event_id, coords)
      }
    }

    const prevIds = prevTransactionIdsRef.current
    const newTransactions = liveData.transactions.filter((t) => !prevIds.has(t.order_id))

    // Update tracked IDs
    const nextIds = new Set(prevIds)
    liveData.transactions.forEach((t) => nextIds.add(t.order_id))
    prevTransactionIdsRef.current = nextIds

    // Skip blips for the initial historical batch
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false
      return
    }

    if (newTransactions.length === 0) return

    // Push blips with staggered start times
    const now = performance.now()
    newTransactions.forEach((tx, index) => {
      const coords = vendorCoords.get(tx.vendor_event_id)
      if (!coords) return

      activeBlipsRef.current.push({
        id: `${tx.order_id}-${now}`,
        lng: coords[0],
        lat: coords[1],
        startTime: now + index * 120,
        dollars: tx.dollars,
      })
    })

    // Kick off the animation loop if not already running
    if (!blipAnimFrameRef.current) {
      blipAnimFrameRef.current = requestAnimationFrame(animateBlips)
    }
  }, [liveData?.transactions, boundaryBounds, toMapCoords, mapConfig?.vendor_locations, animateBlips])

  // Heatmap layer — store in ref for style.load re-add
  // We store the latest heatmap data in a ref so the style.load callback always has current data
  const heatmapDataRef = useRef<{ show: boolean; vendors: typeof heatmapVendors }>({
    show: showHeatmap,
    vendors: heatmapVendors,
  })
  heatmapDataRef.current = { show: showHeatmap, vendors: heatmapVendors }

  useEffect(() => {
    const map = mapRef.current
    if (!map || !boundaryBounds) return

    const cleanup = () => {
      if (!mapRef.current) return
      const m = mapRef.current
      safeRemoveLayer(m, 'live-heatmap')
      safeRemoveLayer(m, 'live-heatmap-points')
      safeRemoveSource(m, 'live-heatmap-source')
      markersRef.current.forEach((mk) => {
        mk.getElement().style.opacity = ''
      })
    }

    if (!showHeatmap || heatmapVendors.length === 0) {
      addHeatmapRef.current = null
      cleanup()
      return
    }

    // Compute max for normalization
    const maxOrders = Math.max(...heatmapVendors.map((v) => v.num_orders), 1)

    const features = heatmapVendors
      .filter((v) => v.x != null && v.y != null)
      .map((v) => {
        const coords = toMapCoords(v.x!, v.y!)
        if (!coords) return null
        return {
          type: 'Feature' as const,
          properties: {
            intensity: v.num_orders / maxOrders,
            label: v.vendor_name,
          },
          geometry: {
            type: 'Point' as const,
            coordinates: coords,
          },
        }
      })
      .filter(Boolean)

    const geojsonData = { type: 'FeatureCollection' as const, features }

    // Dim vendor markers
    markersRef.current.forEach((mk) => {
      mk.getElement().style.opacity = '0.3'
    })

    const addHeatmapLayer = () => {
      if (!mapRef.current) return
      const m = mapRef.current

      // If heatmap was turned off by the time style.load fires, skip
      if (!heatmapDataRef.current.show) return

      safeRemoveLayer(m, 'live-heatmap')
      safeRemoveLayer(m, 'live-heatmap-points')
      safeRemoveSource(m, 'live-heatmap-source')

      m.addSource('live-heatmap-source', {
        type: 'geojson',
        data: geojsonData as any,
      })

      m.addLayer({
        id: 'live-heatmap',
        type: 'heatmap',
        source: 'live-heatmap-source',
        paint: {
          'heatmap-weight': ['interpolate', ['linear'], ['get', 'intensity'], 0, 0, 1, 1],
          'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 10, 1, 18, 3],
          'heatmap-color': [
            'interpolate', ['linear'], ['heatmap-density'],
            0, 'rgba(33,102,172,0)',
            0.15, 'rgba(103,169,207,0.6)',
            0.3, 'rgba(209,229,240,0.7)',
            0.5, 'rgba(253,219,199,0.8)',
            0.7, 'rgba(239,138,98,0.85)',
            1, 'rgba(178,24,43,0.9)',
          ],
          'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 10, 30, 14, 50, 18, 80],
          'heatmap-opacity': 0.75,
        },
      })

      m.addLayer({
        id: 'live-heatmap-points',
        type: 'circle',
        source: 'live-heatmap-source',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['get', 'intensity'], 0, 4, 0.5, 8, 1, 14],
          'circle-color': [
            'interpolate', ['linear'], ['get', 'intensity'],
            0, 'rgb(103,169,207)',
            0.3, 'rgb(253,219,199)',
            0.6, 'rgb(239,138,98)',
            1, 'rgb(178,24,43)',
          ],
          'circle-stroke-color': 'white',
          'circle-stroke-width': 1.5,
          'circle-opacity': 0.9,
        },
      })
    }

    addHeatmapRef.current = addHeatmapLayer

    if (map.isStyleLoaded()) {
      addHeatmapLayer()
    } else {
      map.once('load', addHeatmapLayer)
    }

    return () => {
      addHeatmapRef.current = null
      cleanup()
    }
  }, [showHeatmap, heatmapVendors, boundaryBounds, toMapCoords])

  return (
    <div className="relative w-full h-full">
      <div ref={mapContainerRef} className="w-full h-full" />
      {!resolvedCenter && (
        <div className="absolute inset-0 flex items-center justify-center bg-neutral-100 dark:bg-neutral-950">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        </div>
      )}
    </div>
  )
}

// ────────────────────── Stats Bar ──────────────────────

function StatsBar({ liveData, currency: _currency }: { liveData: LiveStatsResponse | null; currency: string }) {
  const trending = liveData?.trending
  const activeCustomers = liveData?.active_customers

  const trendIcon = () => {
    switch (trending?.status) {
      case 'spike': return <Zap className="w-4 h-4" />
      case 'rising': return <TrendingUp className="w-4 h-4" />
      case 'declining': return <TrendingDown className="w-4 h-4" />
      default: return <Minus className="w-4 h-4" />
    }
  }

  const trendColor = () => {
    switch (trending?.status) {
      case 'spike': return 'bg-red-500/20 text-red-600 dark:text-red-400'
      case 'rising': return 'bg-green-500/20 text-green-600 dark:text-green-400'
      case 'declining': return 'bg-blue-500/20 text-blue-600 dark:text-blue-400'
      default: return 'bg-neutral-100 dark:bg-white/10 text-neutral-500 dark:text-white/60'
    }
  }

  const trendIconColor = () => {
    switch (trending?.status) {
      case 'spike': return 'text-red-600 dark:text-red-400'
      case 'rising': return 'text-green-600 dark:text-green-400'
      case 'declining': return 'text-blue-600 dark:text-blue-400'
      default: return 'text-neutral-500 dark:text-white/60'
    }
  }

  const trendLabel = () => {
    switch (trending?.status) {
      case 'spike': return 'Surge'
      case 'rising': return 'Rising'
      case 'declining': return 'Declining'
      default: return 'Steady'
    }
  }

  return (
    <div className="bg-white/80 dark:bg-black/40 backdrop-blur-xl border border-neutral-200/60 dark:border-white/10 rounded-xl p-3 shadow-2xl shrink-0">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {/* Active Customers */}
        <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-neutral-100/60 dark:bg-white/5">
          <Users className="w-4 h-4 text-blue-500 dark:text-blue-400 shrink-0" />
          <div className="min-w-0">
            <p className="text-[10px] text-neutral-500 dark:text-white/50 uppercase tracking-wider">Customers</p>
            <p className="text-base font-bold text-neutral-900 dark:text-white">
              {activeCustomers?.count ?? '—'}
            </p>
          </div>
        </div>

        {/* Orders Per Minute */}
        <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-neutral-100/60 dark:bg-white/5">
          <ShoppingBag className="w-4 h-4 text-purple-500 dark:text-purple-400 shrink-0" />
          <div className="min-w-0">
            <p className="text-[10px] text-neutral-500 dark:text-white/50 uppercase tracking-wider">Orders/min</p>
            <p className="text-base font-bold text-neutral-900 dark:text-white">
              {trending?.current_orders_per_minute?.toFixed(1) ?? '—'}
            </p>
          </div>
        </div>

        {/* Trend Status */}
        <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-neutral-100/60 dark:bg-white/5">
          <div className={`shrink-0 ${trendIconColor()}`}>
            {trendIcon()}
          </div>
          <div className="min-w-0">
            <p className="text-[10px] text-neutral-500 dark:text-white/50 uppercase tracking-wider">Trend</p>
            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[11px] font-semibold ${trendColor()}`}>
              {trendLabel()}
            </span>
          </div>
        </div>

        {/* Change % */}
        <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-neutral-100/60 dark:bg-white/5">
          <div className={`shrink-0 ${(trending?.change_pct ?? 0) >= 0 ? 'text-green-500 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
            {(trending?.change_pct ?? 0) >= 0
              ? <ArrowUpRight className="w-4 h-4" />
              : <ArrowDownRight className="w-4 h-4" />
            }
          </div>
          <div className="min-w-0">
            <p className="text-[10px] text-neutral-500 dark:text-white/50 uppercase tracking-wider">Change</p>
            <p className="text-base font-bold text-neutral-900 dark:text-white">
              {trending?.change_pct != null
                ? `${trending.change_pct >= 0 ? '+' : ''}${trending.change_pct.toFixed(1)}%`
                : '—'
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

// ────────────────────── Transaction Feed ──────────────────────

function TransactionFeed({
  transactions,
  currency: _currency,
  activeWindow,
}: {
  transactions: LiveStatsResponse['transactions']
  currency: string
  activeWindow: number
}) {
  const feedRef = useRef<HTMLDivElement>(null)
  const isAtTopRef = useRef(true)

  const handleScroll = () => {
    if (feedRef.current) {
      isAtTopRef.current = feedRef.current.scrollTop < 10
    }
  }

  // Auto-scroll to top when new transactions arrive (if user hasn't scrolled down)
  useEffect(() => {
    if (isAtTopRef.current && feedRef.current) {
      feedRef.current.scrollTop = 0
    }
  }, [transactions.length])

  const formatTimeAgo = (dateStr: string) => {
    const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000)
    if (seconds < 10) return 'just now'
    if (seconds < 60) return `${seconds}s ago`
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    return `${hours}h ago`
  }

  const statusColor = (status: string) => {
    switch (status) {
      case 'completed':
      case 'delivered': return 'bg-green-500/20 text-green-600 dark:text-green-400'
      case 'accepted': return 'bg-blue-500/20 text-blue-600 dark:text-blue-400'
      default: return 'bg-neutral-100 dark:bg-white/10 text-neutral-500 dark:text-white/50'
    }
  }

  return (
    <div className="bg-white/80 dark:bg-black/40 backdrop-blur-xl border border-neutral-200/60 dark:border-white/10 rounded-xl shadow-2xl flex flex-col shrink-0" style={{ maxHeight: '40vh' }}>
      <div className="px-4 py-2.5 border-b border-neutral-200/60 dark:border-white/10 shrink-0">
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">
          Live Transactions
          {transactions.length > 0 && (
            <span className="ml-2 text-xs font-normal text-neutral-400 dark:text-white/40">
              ({transactions.length})
            </span>
          )}
        </h3>
      </div>
      <div
        ref={feedRef}
        onScroll={handleScroll}
        className="overflow-y-auto divide-y divide-neutral-100 dark:divide-white/5"
      >
        {transactions.length === 0 ? (
          <div className="p-8 text-center text-neutral-400 dark:text-white/30 text-sm">
            No transactions in the past {activeWindow} minutes.
          </div>
        ) : (
          transactions.map((tx) => (
            <div
              key={tx.order_id}
              className="flex items-center justify-between px-4 py-2 hover:bg-neutral-50 dark:hover:bg-white/5 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                    {tx.vendor_name || 'Unknown'}
                  </span>
                  {tx.booth && (
                    <span className="text-xs text-neutral-400 dark:text-white/30 shrink-0">
                      {tx.booth}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  {tx.order_display_id && (
                    <span className="text-xs text-neutral-400 dark:text-white/40 font-mono">
                      {tx.order_display_id}
                    </span>
                  )}
                  <span className="text-xs text-neutral-400 dark:text-white/30">
                    {tx.num_items} item{tx.num_items !== 1 ? 's' : ''}
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${statusColor(tx.status)}`}>
                    {tx.status}
                  </span>
                </div>
              </div>
              <div className="text-right shrink-0 ml-3">
                <p className="text-sm font-semibold text-neutral-900 dark:text-white">
                  ${tx.dollars.toFixed(2)}
                </p>
                <p className="text-[11px] text-neutral-400 dark:text-white/30">
                  {formatTimeAgo(tx.created_at)}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

// ────────────────────── Product Leaderboard ──────────────────────

function ProductLeaderboard({
  products,
  currency: _currency,
  isLoading,
}: {
  products: LiveStatsResponse['product_leaderboard']
  currency: string
  isLoading?: boolean
}) {
  const medalEmoji = (rank: number) => {
    if (rank === 0) return '🥇'
    if (rank === 1) return '🥈'
    if (rank === 2) return '🥉'
    return null
  }

  return (
    <div className="bg-white/80 dark:bg-black/40 backdrop-blur-xl border border-neutral-200/60 dark:border-white/10 rounded-xl shadow-2xl shrink-0">
      <div className="px-4 py-2.5 border-b border-neutral-200/60 dark:border-white/10">
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">Top Products</h3>
      </div>
      <div className="divide-y divide-neutral-100 dark:divide-white/5">
        {isLoading ? (
          <div className="p-5 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-neutral-300 dark:text-white/30" />
          </div>
        ) : products.length === 0 ? (
          <div className="p-5 text-center text-neutral-400 dark:text-white/30 text-sm">
            No product data yet
          </div>
        ) : (
          products.map((product, idx) => (
            <div key={idx} className="flex items-center gap-3 px-4 py-2">
              <span className="w-6 text-center text-sm shrink-0">
                {medalEmoji(idx) || (
                  <span className="text-xs text-neutral-400 dark:text-white/40 font-medium">#{idx + 1}</span>
                )}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                  {product.item_name || 'Unknown Item'}
                </p>
                <p className="text-xs text-neutral-400 dark:text-white/40 truncate">{product.vendor_name}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-semibold text-neutral-900 dark:text-white">
                  {product.qty_sold} sold
                </p>
                <p className="text-xs text-neutral-400 dark:text-white/40">
                  ${product.dollars_revenue.toFixed(2)}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  )
}

// ────────────────────── Top Movers ──────────────────────

function TopMovers({
  movers,
}: {
  movers: LiveStatsResponse['trending']['top_movers']
}) {
  if (movers.length === 0) return null

  return (
    <div className="bg-white/80 dark:bg-black/40 backdrop-blur-xl border border-neutral-200/60 dark:border-white/10 rounded-xl shadow-2xl shrink-0">
      <div className="px-4 py-2.5 border-b border-neutral-200/60 dark:border-white/10">
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-white">Top Movers</h3>
      </div>
      <div className="divide-y divide-neutral-100 dark:divide-white/5">
        {movers.map((mover) => (
          <div key={mover.vendor_event_id} className="flex items-center justify-between px-4 py-2">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                {mover.vendor_name || 'Unknown'}
              </p>
              {mover.booth && (
                <p className="text-xs text-neutral-400 dark:text-white/30 truncate">{mover.booth}</p>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0 ml-3">
              <span className="text-xs text-neutral-400 dark:text-white/40">
                {mover.previous_rate.toFixed(1)} → {mover.current_rate.toFixed(1)}/min
              </span>
              <span className={`inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-semibold ${
                mover.change_pct >= 0
                  ? 'bg-green-500/20 text-green-600 dark:text-green-400'
                  : 'bg-red-500/20 text-red-600 dark:text-red-400'
              }`}>
                {mover.change_pct >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {Math.abs(mover.change_pct).toFixed(0)}%
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
